/**
 * Utility functions for transforming hotel offer data to map marker format.
 * Shared between streaming events and historical carousel restoration.
 */
import type { HotelSearchOffer } from 'tripPlanner/api/types'
import { getCurrentIntl } from 'i18n/util/currentIntl'

export type MapCoordinate = { lat: number; lng: number }

export const MARKER_TYPE_STOP = 'stop' as const
export const MARKER_TYPE_ITEM = 'item' as const
export const MARKER_TYPE_CHAT_HOTEL = 'chat-hotel' as const
export const MARKER_TYPE_SUGGESTED = 'suggested' as const
export const MARKER_TYPE_EXPLORE = 'explore' as const
export const MARKER_TYPE_EXPLORE_HOTEL = 'explore-hotel' as const
export const MARKER_TYPE_CHAT_EXPERIENCE = 'chat-experience' as const
export const MARKER_TYPE_EXPLORE_EXPERIENCE = 'explore-experience' as const

export type MarkerType =
  | typeof MARKER_TYPE_STOP
  | typeof MARKER_TYPE_ITEM
  | typeof MARKER_TYPE_CHAT_HOTEL
  | typeof MARKER_TYPE_SUGGESTED
  | typeof MARKER_TYPE_EXPLORE
  | typeof MARKER_TYPE_EXPLORE_HOTEL
  | typeof MARKER_TYPE_CHAT_EXPERIENCE
  | typeof MARKER_TYPE_EXPLORE_EXPERIENCE

const MARKER_TYPES: ReadonlySet<string> = new Set<MarkerType>([
  MARKER_TYPE_STOP,
  MARKER_TYPE_ITEM,
  MARKER_TYPE_CHAT_HOTEL,
  MARKER_TYPE_SUGGESTED,
  MARKER_TYPE_EXPLORE,
  MARKER_TYPE_EXPLORE_HOTEL,
  MARKER_TYPE_CHAT_EXPERIENCE,
  MARKER_TYPE_EXPLORE_EXPERIENCE,
])

export const isMarkerType = (value: string | undefined): value is MarkerType =>
  value !== undefined && MARKER_TYPES.has(value)

export const MARKER_SOURCE_ITINERARY = 'itinerary' as const
export const MARKER_SOURCE_SUGGESTED = 'suggested' as const
export const MARKER_SOURCE_CHAT_HOTEL = 'chat-hotel' as const
export const MARKER_SOURCE_EXPLORE = 'explore' as const
export const MARKER_SOURCE_EXPLORE_HOTEL = 'explore-hotel' as const
export const MARKER_SOURCE_CHAT_EXPERIENCE = 'chat-experience' as const
export const MARKER_SOURCE_EXPLORE_EXPERIENCE = 'explore-experience' as const

export type MarkerSource =
  | typeof MARKER_SOURCE_ITINERARY
  | typeof MARKER_SOURCE_SUGGESTED
  | typeof MARKER_SOURCE_CHAT_HOTEL
  | typeof MARKER_SOURCE_EXPLORE
  | typeof MARKER_SOURCE_EXPLORE_HOTEL
  | typeof MARKER_SOURCE_CHAT_EXPERIENCE
  | typeof MARKER_SOURCE_EXPLORE_EXPERIENCE

/**
 * MapMarkerComponent is the format expected by ChatMapWidget.
 */
export interface MapMarkerComponent extends MapCoordinate {
  label?: string
  description?: string
  placeId?: string
  markerId?: string
  title?: string
  imageIds?: Array<string>
  rating?: number
  userRatingCount?: number
  photoName?: string
  markerType?: MarkerType
  categoryLabel?: string
  source?: MarkerSource
  offerId?: string
  imageId?: string
  offerPrice?: number
  isOriginStop?: boolean
}

export const hasValidLatLng = (lat: unknown, lng: unknown): boolean => {
  return (
    typeof lat === 'number' &&
    typeof lng === 'number' &&
    Number.isFinite(lat) &&
    Number.isFinite(lng)
  )
}

export const isValidCoordinate = <T extends MapCoordinate>(coord?: T): coord is T => {
  return Boolean(coord && hasValidLatLng(coord.lat, coord.lng))
}

type OfferWithProperty = {
  property?: {
    latitude?: number
    longitude?: number
    name?: string
  }
}

type OfferWithLocation = {
  location?: {
    latitude?: number
    longitude?: number
  }
}

const isOfferWithProperty = (offer: App.AnyOffer): offer is App.AnyOffer & OfferWithProperty => {
  return typeof offer === 'object' && offer !== null && 'property' in offer
}

const isOfferWithLocation = (offer: App.AnyOffer): offer is App.AnyOffer & OfferWithLocation => {
  return typeof offer === 'object' && offer !== null && 'location' in offer
}

export function hasValidCoordinates(offer: HotelSearchOffer): offer is HotelSearchOffer & {
  coordinates: { lat: number; lng: number }
} {
  return (
    offer.coordinates !== undefined &&
    hasValidLatLng(offer.coordinates.lat, offer.coordinates.lng)
  )
}

/**
 * Transforms an array of HotelSearchOffer to MapMarkerComponent format.
 * Filters out offers without valid coordinates.
 */
export function transformOffersToMarkers(offers: Array<HotelSearchOffer>): Array<MapMarkerComponent> {
  return offers
    .filter(hasValidCoordinates)
    .map((offer) => ({
      lat: offer.coordinates.lat,
      lng: offer.coordinates.lng,
      label: offer.name,
      markerId: offer.id,
      offerId: offer.id,
      markerType: MARKER_TYPE_CHAT_HOTEL,
      source: MARKER_SOURCE_CHAT_HOTEL,
    }))
}

/**
 * Transforms an array of HotelSearchOffer to MapMarkerComponent format for experiences.
 * Filters out offers without valid coordinates.
 */
export function transformOffersToExperienceMarkers(offers: Array<HotelSearchOffer>): Array<MapMarkerComponent> {
  return offers
    .filter(hasValidCoordinates)
    .map((offer) => ({
      lat: offer.coordinates.lat,
      lng: offer.coordinates.lng,
      label: offer.name,
      markerId: offer.id,
      offerId: offer.id,
      markerType: MARKER_TYPE_CHAT_EXPERIENCE,
      source: MARKER_SOURCE_CHAT_EXPERIENCE,
    }))
}

/**
 * Normalises App.AnyOffer (from useOffers hook) to HotelSearchOffer format.
 * This is used when "Map view" is clicked on a carousel to populate map state.
 */
export function normaliseOfferForMap(offer: App.AnyOffer): HotelSearchOffer {
  const intl = getCurrentIntl()
  const unknownHotelLabel = intl.formatMessage({
    id: 'DOUm6iuNHlEusrDLKeXt',
    defaultMessage: 'Unknown Hotel',
    description: 'Fallback hotel name shown on map markers when the hotel name is missing',
  })
  const unknownOfferLabel = intl.formatMessage({
    id: 'tPOt0HEoJor_JW9Uc9zF',
    defaultMessage: 'Unknown Offer',
    description: 'Fallback offer name shown on map markers when the offer name is missing',
  })

  // Hotel offers have property.latitude/longitude (flattened client structure)
  if (isOfferWithProperty(offer) && offer.property) {
    const property = offer.property
    return {
      id: offer.id,
      name: property.name ?? offer.name ?? unknownHotelLabel,
      coordinates: property.latitude !== undefined && property.longitude !== undefined
        ? { lat: property.latitude, lng: property.longitude }
        : undefined,
    }
  }

  // Experience offers have location.latitude/longitude
  if (isOfferWithLocation(offer) && offer.location) {
    const location = offer.location
    return {
      id: offer.id,
      name: offer.name ?? unknownOfferLabel,
      coordinates: location.latitude !== undefined && location.longitude !== undefined
        ? { lat: location.latitude, lng: location.longitude }
        : undefined,
    }
  }

  // Fallback for other offer types
  return {
    id: offer.id,
    name: offer.name ?? unknownOfferLabel,
  }
}

/**
 * Batch normalises an array of App.AnyOffer to HotelSearchOffer format.
 */
export function normaliseOffersForMap(offers: Array<App.AnyOffer>): Array<HotelSearchOffer> {
  return offers.map(normaliseOfferForMap)
}
